import { PartnerEndpoints } from '../../../utils/endpoints';
import { Permissions } from '../../../utils/permissions';
import { Constants } from '../../../utils/constants';
import { formatCount, formatNaira, grossAmount } from '../../../utils/money';
import { formatLagos } from '../../../utils/datetime';
import { useAuth } from '../../../AuthContext';
import PageHeader from '../../shell/PageHeader';
import {
  Alert,
  DataTable,
  DateRangeFilter,
  ExportButton,
  FilterSelect,
  Pagination,
  SearchInput,
  StatusPill,
  TableCard,
} from '../../ui';
import { useServerTable } from '../../../hooks/useServerTable';
import { useExport } from '../../../hooks/useExport';

const buildColumns = ({ dateFilter, statusFilter }) => [
  {
    key: 'created_at',
    header: 'Date (WAT)',
    sortKey: 'created_at',
    sortDefault: 'desc',
    filter: dateFilter,
    render: (row) => <span className="whitespace-nowrap">{formatLagos(row.created_at)}</span>,
  },
  { key: 'pan', header: 'Payer PAN', render: (row) => row.pan || '—' },
  { key: 'direction', header: 'Direction', className: 'capitalize', render: (row) => row.direction || '—' },
  {
    key: 'status',
    header: 'Status',
    sortKey: 'status',
    filter: statusFilter,
    render: (row) => <StatusPill status={row.status} />,
  },
  { key: 'gross', header: 'Gross', align: 'right', render: (row) => formatNaira(grossAmount(row)) },
  { key: 'discount_amount', header: 'Discount', align: 'right', render: (row) => formatNaira(row.discount_amount) },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    sortKey: 'amount',
    sortDefault: 'desc',
    render: (row) => formatNaira(row.amount),
  },
  { key: 'merchant', header: 'Merchant', render: (row) => row.merchant || '—' },
  { key: 'reference', header: 'Reference', render: (row) => row.reference || '—' },
];

const Transactions = () => {
  const { hasPermission } = useAuth();
  const canExport = hasPermission(Permissions.transactionsExport);
  const exporter = useExport(PartnerEndpoints.transactionsExport, 'We could not export your transactions. Please try again.');
  const table = useServerTable(PartnerEndpoints.transactions, {
    sort: '-created_at',
    filters: { status: '' },
    fallbackError: 'We could not load your transactions. Please try again.',
  });

  const columns = buildColumns({
    dateFilter: (
      <DateRangeFilter
        label="Date"
        from={table.from}
        to={table.to}
        onFromChange={table.setFrom}
        onToChange={table.setTo}
      />
    ),
    statusFilter: (
      <FilterSelect
        value={table.filters.status ?? ''}
        onChange={(value) => table.setFilter('status', value)}
        options={Constants.transactionStatuses}
        label="All"
        ariaLabel="Filter by status"
      />
    ),
  });

  return (
    <>
      <PageHeader
        title="Transactions"
        breadcrumbs={[{ label: 'Home', to: '/layout/dashboard' }, { label: 'Transactions' }]}
      />
      {exporter.error && (
        <Alert variant="danger" onDismiss={exporter.clearError} className="mb-6">
          {exporter.error}
        </Alert>
      )}
      <TableCard
        title="Transactions"
        summary={{ label: 'Transactions', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <>
            <SearchInput
              value={table.search}
              onChange={table.setSearch}
              placeholder="Search reference or PAN"
            />
            {canExport && (
              <ExportButton
                onClick={() => exporter.runExport(table.params)}
                loading={exporter.exporting}
                disabled={table.loading || table.total === 0 || table.invalidDateRange}
                title={`Exports all ${formatCount(table.total)} matching transactions, not just this page`}
              />
            )}
          </>
        }
        footer={
          <Pagination
            page={table.page}
            pageCount={table.pageCount}
            pageSize={table.pageSize}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(row) => row.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{
            title: 'No transactions',
            message: 'No transactions found for the selected filters.',
          }}
        />
      </TableCard>
    </>
  );
};

export default Transactions;
